const RECONCILIATION_CHANNEL_NAME = "reconciliation-sync";
const RECONCILIATION_STORAGE_KEY = "reconciliation-sync";

export interface ReconciliationBroadcastEvent {
  type: "reconciliation-updated";
  scope: "manual-match";
  movementIds: string[];
  groupId?: string;
  stepId?: string;
  timestamp: number;
}

let reconciliationChannel: BroadcastChannel | null = null;

function getBroadcastChannel() {
  if (
    typeof window === "undefined" ||
    typeof BroadcastChannel === "undefined"
  ) {
    return null;
  }

  if (!reconciliationChannel) {
    reconciliationChannel = new BroadcastChannel(RECONCILIATION_CHANNEL_NAME);
  }

  return reconciliationChannel;
}

function isReconciliationBroadcastEvent(
  value: unknown,
): value is ReconciliationBroadcastEvent {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    (value as ReconciliationBroadcastEvent).type === "reconciliation-updated"
  );
}

export function broadcastReconciliationUpdate(
  event: Omit<ReconciliationBroadcastEvent, "timestamp">,
) {
  if (typeof window === "undefined") {
    return;
  }

  const payload: ReconciliationBroadcastEvent = {
    ...event,
    timestamp: Date.now(),
  };

  getBroadcastChannel()?.postMessage(payload);
  window.localStorage.setItem(
    RECONCILIATION_STORAGE_KEY,
    JSON.stringify(payload),
  );
}

export function subscribeToReconciliationUpdates(
  onMessage: (event: ReconciliationBroadcastEvent) => void,
) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const channel = getBroadcastChannel();

  const handleMessage = (value: unknown) => {
    if (!isReconciliationBroadcastEvent(value)) {
      return;
    }

    onMessage(value);
  };

  const handleChannelMessage = (event: MessageEvent<unknown>) => {
    handleMessage(event.data);
  };

  const handleStorage = (event: StorageEvent) => {
    if (
      event.key !== RECONCILIATION_STORAGE_KEY ||
      typeof event.newValue !== "string"
    ) {
      return;
    }

    try {
      handleMessage(JSON.parse(event.newValue));
    } catch {
      return;
    }
  };

  channel?.addEventListener("message", handleChannelMessage);
  window.addEventListener("storage", handleStorage);

  return () => {
    channel?.removeEventListener("message", handleChannelMessage);
    window.removeEventListener("storage", handleStorage);
  };
}
